import { PromoErrorType } from './common.types';
import { SelectedGiftCardType } from './giftCard.types';

export enum OnlineBookingMeth {
  Everyone = 0,
  NewClientsOnly = 1,
  ExistingClientsOnly = 2
}

export interface ServiceInterface {
  id: number;
  name: string;
  svc_price: number;
  retail: number;
  typid: number;
  duration: string | null;
  dscr: string;
  online_booking_meth: OnlineBookingMeth;
  allowed_cl_prov_meth: number;
  allowed_cl_resc_meth: number;
  min_attendees: number;
  max_attendees: number;
  allow_book: boolean;
  allow_request: boolean;
  gender: 'both' | 'male' | 'female';
  package_only: number;
  public_pricing_line_1: null | string;
  public_pricing_line_2: null | string;
  public_pricing_line_3: null | string;
  pg_require: number;
  pg_authorized: number;
  is_client_eligible: number;
  multiperson_allow: number;
  req_provider: boolean;
  req_resource: boolean;
  cl_fav: number;
  fav_id: number;
}

export interface MasterServiceInterface {
  id: number;
  name: string;
  dscr: string;
  is_master: boolean;
  retail: number;
  cat_name: string;
  cat_id: number;
  duration: null | string;
  svc_list: ServiceInterface[];
}

export interface CombinedServiceInterface
  extends ServiceInterface,
    MasterServiceInterface {}

export interface AddonInterface {
  id: number;
  name: string;
  duration: string;
  dscr: string | null;
  retail: string;
  req_provider: boolean;
  req_resource: boolean;
  wait_time: number;
}

export interface ExtendedAddonInterface extends AddonInterface {
  parentServiceId: number;
  svc_price?: number;
  online_booking_meth: OnlineBookingMeth;
}

export interface ProviderInterface {
  id: number;
  name: string;
  public_name?: string;
  public_title?: string;
  public_quote?: string;
  public_dscr?: string;
  public_photo_url?: string;
  img?: string;
  description?: string;
  service?: number;
}

export interface CategoryInterface {
  id: number;
  name: string;
  icon_id?: string | null;
}

export enum SpecialServicesTabsIds {
  Review = 0,
  Recent = 1,
  Favorites = 2,
  Popular = 3,
  Consultation = 4
}

export enum Gender {
  Any = 0,
  Male = 1,
  Female = 2
}

export interface VisitedServices {
  id: number;
  attendees: number | null;
  provider: number;
}

export interface GetAppointmentsParamsInterface {
  locid: number;
  services: VisitedServices[];
  from_date?: string;
  to_date?: string;
  from_time?: string;
  to_time?: string;
  exact_time?: string;
  appt_id?: number;
  appts_number?: number;
  days_mask?: number;
  provider_gender?: Gender;
  attendees_count?: number;
  skip_rs_variations?: number;
}

export interface AppointmentService {
  rel_id: number;
  svc_num: number;
  svc_name: string;
  svc_start_time: string;
  svc_end_time: string;
  start_time: number;
  end_time: number;
  duration: number;
  qty: number;
  svc_order: number;
  assigned_em: number[];
  assigned_em_2: {
    em_id: number;
    em_name: string;
    em_title: string;
    em_requested: boolean;
  }[];
  appt_sv_attendees: number[];
  automatically_added: number;
  do_not_show_public: number;
  has_custom_duration: number;
  has_squeezed_services: number;
  is_gap: boolean;
  sv_placement: number;
  svc_price: number;
  price: number;
  price_orig: number;
  adjusted_price: number;
  charge_now: number;
  em_name: string[];
  public_title: string[];
  public_photo_url: string[];
  em_requested: boolean[];
}

export interface AppointmentInterface {
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  startdatetime: number;
  enddatetime: number;
  duration: number;
  tz_offset: number;
  store: number;
  foundapptid: string;
  appt_price: number;
  appt_price_orig: number;
  charge_now: number;
  qty: number;
  amount_total: number;
  tax_amount_total: number;
  name: string;
  services: AppointmentService[];
}

export interface AppointmentDataInterface
  extends Record<string, AppointmentInterface> {}

export enum ServiceType {
  All = 'all',
  Recent = 'recent',
  Eligible = 'eligible',
  Interested = 'interested',
  Recommended = 'recommended',
  Popular = 'featured'
}

export type ServiceTypeVariant = ServiceType;

export interface DiscountCode {
  code_id: number;
  name: string;
  discount_prc: number;
  is_valid: boolean;
  promo_error_type?: PromoErrorType;
}

interface AssignedResource {
  required: boolean;
  appt_id: number;
  template_id: number;
  requested: boolean;
  assigned_rs_id: number;
  assigned_rs_name: string;
  tbr_flag: boolean;
  group_type: number;
  group_id: number;
  customized: boolean;
  mandatory: boolean;
  appt_sv_id: number;
  exclusive: boolean;
  rs_id: number;
}

interface AssignedEmployee {
  required: boolean;
  appt_id: number;
  template_id: number;
  assigned_em_id: number;
  empl_id: number;
  requested: boolean;
  tbr_flag: boolean;
  group_type: number;
  group_id: number;
  customized: boolean;
  mandatory: boolean;
  appt_sv_id: number;
  assigned_em_title?: string;
  exclusive: boolean;
  assigned_em_name: string;
}

export interface ServiceCheckoutData {
  sv_end_dt: string;
  combo_sv_time_type: number;
  sessions_count: number;
  gender: number;
  effective_end_dt: string;
  price_adjustment_price_level: number;
  max_ages: number;
  pre_sv_scope: number;
  post_sv_msg: string;
  hair_system_id: number;
  price_deposit_member: number;
  appt_id: number;
  provider_gender: number;
  price_orig: number;
  avail_type_id: number;
  pricing_type: number;
  pkg_item_id: number;
  min_attendees: number;
  cancelation_fee_member: number;
  price_adjustment: number;
  checked_out: boolean;
  template_id: number;
  post_sv_time: string;
  post_sv_skip: boolean;
  sv_type: string;
  sv_name: string;
  total_duration: string;
  cancelation_fee: number;
  post_sv_scope: number;
  multiperson_allow: number;
  processed_pkg_qty: number;
  price_dropins_member: number;
  public_comment: string;
  ignore_gap_rs: boolean;
  ignore_gap_pr: boolean;
  pkg_visit_item_id: number;
  sv_start_dt: string;
  sv_finish_duration_net: string;
  duration_ex_id: number;
  sessions_type: number;
  age_restrictions: number;
  has_finish_time: boolean;
  quantity_def: number;
  has_squeezed_services: boolean;
  assigned_rs: AssignedResource[];
  assigned_em: AssignedEmployee[];
  is_gap: boolean;
  do_not_show_public: boolean;
  sv_finish_start_dt: string;
  pre_sv_time_type: number;
  gross_finish_duration: string;
  price_dropins: number;
  client_end_dt: string;
  waiting_attendees_count: number;
  gap_duration: string;
  sv_duration_net: string;
  quantity: number;
  price_deposit: number;
  sv_position: number;
  gross_sv_duration: string;
  combo_sv_finish_time_type: number;
  min_ages: number;
  sv_smart_effective_id: number;
  automatically_added: boolean;
  has_adjusted_price: boolean;
  placed_related_to_sv: number;
  gross_duration: string;
  appt_sv_id: number;
  booked_with_combo_sv: number;
  booked_with_combo: number;
  sv_duration_effective: string;
  client_start_dt: string;
  sv_id: number;
  sv_placement: number;
  pre_sv_time: string;
  pre_sv_skip: boolean;
  attendees_count: number;
  pre_sv_msg: string;
  sv_order: number;
  can_modify_quantity: boolean;
  price_adjustment_amount: number;
  price_member: number;
  post_sv_time_type: number;
  sv_finish_end_dt: string;
  hair_status: number;
  has_custom_duration: boolean;
  payment_due: number;
  effective_start_dt: string;
  max_attendees: number;
  adjusted_price: number;
  price: number;
}

export interface AppointmentCheckoutData {
  confirmed_on: string;
  non_squeezed_services_count: number;
  old_em_name: string;
  guarantee_amount: number;
  is_late_cancel: boolean;
  current_sv_name: string;
  waiting_status: number;
  appt_sv: ServiceCheckoutData[];
  appt_id: number;
  store_name: string;
  ref_num: number;
  current_rs_num: number;
  orig_sv_num: number;
  current_em_name: string;
  skip_notifications: number;
  orig_em_num: number;
  created_by_empl: number;
  is_separated: boolean;
  template_id: number;
  status: number;
  last_modified_on: string;
  last_modified_by: string;
  occurence_num: number;
  is_template: boolean;
  target_num: number;
  old_sv_num: number;
  old_em_2_num: number;
  old_em_num: number;
  old_em_ts: string;
  orig_store_name: string;
  cl_comment: string;
  amount: number;
  store_id: number;
  is_korvue_xl_appt: boolean;
  appt_end: string;
  old_sv_ts: string;
  checkout_done: boolean;
  referrer_id: number;
  minutes_late: number;
  old_rs_ts: string;
  appt_type: number;
  orig_em_2_num: number;
  store_group_id: number;
  group_id: number;
  created_on: string;
  created_by: string;
  store_group_name: string;
  last_modified_by_empl: number;
  old_em_2_name: string;
  current_sv_num: number;
  old_store_ts: string;
  first_column_id: number;
  current_em_num: number;
  target_type: number;
  target_name: string;
  orig_rs_num: number;
  orig_rs_name: string;
  current_em_2_name: string;
  last_appt_sv_id: number;
  has_sub_request: boolean;
  wasted_at_begin: number;
  first_column_block_id: number;
  checked_in_on: string;
  comment: string;
  appt_start: string;
  first_appt_sv_id: number;
  status_reason: number;
  region_group_id: number;
  invoice_num: number;
  orig_sv_name: string;
  old_rs_name: string;
  status_name: string;
  postponed: boolean;
  wasted_total: number;
  first_column_type: string;
  group_color: string;
  different_location: boolean;
  services_count: number;
  old_rs_num: number;
  current_em_2_num: number;
  openings_cont: number;
  orig_em_2_name: string;
  old_sv_name: string;
  orig_em_name: string;
  first_column_type_2: number;
  default_appt_start: string;
  current_rs_name: string;
  orig_store_num: number;
  deposit_amount: number;
  status_visible_def: boolean;
  rec_pattern_id: number;
  amount_orig: number;
}

export interface BookingConfirmation {
  req_book_confirm: boolean;
  book_confirm_text: string;
  cc_allow_to_skip: number;
  cc_on_file_require: number;
}

export interface BookingParams {
  appt: AppointmentInterface;
  locid: number;
  move_appt_id?: number;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  attendees?: number[];
  attendees_cost?: number[];
  charge_deposit?: number;
  deposit_amt?: number;
  deposit_ccard_id?: number;
  deposit_ccard_cvv?: number;
  deposit_gcard?: SelectedGiftCardType[];
  store_guarantee?: number;
  payment_id?: number;
  guarantee_amt?: number;
  promo_code?: string;
  promo_code_id?: number;
}

export interface ResponseStatusObject {
  isSuccess: boolean;
  message: string;
}

export interface AttendeePriceInterface {
  client_id: number;
  payer_amount: number;
  responsible_client_id: number;
  price_level_id: number;
}

export interface ServicePriceInterface {
  sv_id: number;
  service_num: number;
  price: number;
  price_orig: number;
  price_level_id: number;
  attendees: AttendeePriceInterface[];
}

export interface ApptPriceDataInterface {
  success: boolean;
  price: number;
  price_orig: number;
  services: ServicePriceInterface[];
  multiperson_allow: number;
}

export interface DepositOptionsInterface {
  deposit_amount: number;
  guarantee_amount: number;
  req_deposit: number;
  req_guarantee: number;
  total_appt_amount: number;
}

export enum PickTimeTabType {
  CALENDAR_SEARCH,
  ADVANCED_SEARCH
}
